// Central tunables. Thresholds are deliberately conservative so a face only
// counts as "watching you" when it is genuinely oriented toward the camera.

import type { DrawConfig } from "./draw/types";

export const CONFIG = {
  vision: {
    /** Max simultaneous faces the landmarker will track. */
    maxFaces: 8,
    /** Self-hosted model + wasm (same-origin so COEP + the service worker are happy). */
    modelAssetPath: "/models/face_landmarker.task",
    wasmPath: "/mediapipe/wasm",
    /** GPU is dramatically faster; falls back to CPU automatically on failure. */
    preferGpu: true,
  },

  lockOn: {
    /** Head yaw/pitch (degrees) at which "facing me" confidence reaches 0. */
    headYawZeroDeg: 28,
    headPitchZeroDeg: 24,
    /** Combined eye-look blendshape magnitude at which "gaze centered" reaches 0. */
    gazeZeroMag: 0.7,
    /** A face is "locked on" when lockScore exceeds this. */
    lockThreshold: 0.5,
    /**
     * An animal is "locked on" when its (coarser) lockScore exceeds this. It is
     * lower than the face threshold because the animal score is gated by both
     * detector confidence and a best-effort facing heuristic, so it sits lower
     * overall (there is no true animal gaze model, only an estimate).
     */
    animalLockThreshold: 0.3,
    /** Frames of hysteresis before a lock flips on/off (debounce flicker). */
    lockHysteresis: 3,
    /**
     * EMA weight for the new frame when smoothing lockScore before thresholding
     * (0..1; lower = smoother, more lag). Temporal smoothing measurably improves
     * eye-contact precision on a moving camera (deep-research finding).
     */
    lockSmoothing: 0.45,
    /** Eye-aspect-ratio at/above which eyes read as fully open. */
    earOpen: 0.28,
    /** Eye-aspect-ratio at/below which eyes read as closed (a blink). */
    earClosed: 0.12,
  },

  tracker: {
    /** Max normalized centroid distance to consider two frames the same face. */
    matchRadius: 0.12,
    /** Frames a face can be missing before its track is dropped. */
    maxMissingFrames: 8,
  },

  distance: {
    /**
     * Assumed real horizontal iris diameter (metres). The iris is far more
     * constant across people (~11.7mm, a few percent variance) than the IPD
     * (~20% variance), so iris-diameter ranging is much more accurate.
     */
    realIrisMeters: 0.0117,
    /** Assumed real inter-pupillary distance (metres), used as a fallback. */
    realIpdMeters: 0.063,
    /** Assumed horizontal field of view (degrees) of a typical rear camera. */
    assumedHFovDeg: 62,
    /** Assumed vertical field of view (degrees), for head-elevation geometry. */
    assumedVFovDeg: 48,
    /** Assumed camera height off the ground (metres) when worn in a lapel pocket. */
    phoneHeightMeters: 1.35,
  },

  analysis: {
    /** Only analyse faces that stay locked at least this long (ms), avoids spam. */
    minLockedMsBeforeAnalyse: 900,
    /** Re-analyse the same tracked person at most this often (ms). */
    reanalyseIntervalMs: 15000,
    /** Endpoint (same-origin in production; a mock intercepts it in dev). */
    endpoint: "/api/analyze",
    /** JPEG quality for the cropped face sent to the analyser. */
    cropJpegQuality: 0.8,
    /** Padding factor around the face bbox when cropping for analysis. */
    cropPadding: 0.35,
  },
} as const;

/**
 * Draw Mode configuration. This is the single DrawConfig instance passed into
 * `new DrawMode({ ..., config: DRAW_CONFIG })` from main.ts. It satisfies the
 * DrawConfig contract in src/draw/types.ts, which is the single source of truth
 * for the shape. The drawing phase is 150s (2.5 min = sessionSec 150); the hand
 * input reuses the self-hosted MediaPipe wasm + a hand_landmarker.task model,
 * and the pinch thresholds use hysteresis (pinchUpDist > pinchDownDist) so the
 * air-pen does not chatter down/up at the boundary.
 */
// Dev/testing: ?drawfast collapses the ritual so the report path can be
// exercised in seconds (headless smoke). No effect without the query param.
const DRAW_FAST =
  typeof location !== "undefined" &&
  new URLSearchParams(location.search).has("drawfast");

export const DRAW_CONFIG: DrawConfig = {
  // 150s drawing phase (2.5 min), with a short settle countdown first.
  durationMs: DRAW_FAST ? 6000 : 150000,
  countdownMs: DRAW_FAST ? 600 : 4000,
  hand: {
    modelAssetPath: "/models/hand_landmarker.task",
    wasmPath: "/mediapipe/wasm",
    numHands: 1,
    // pinchThreshold pair: pen goes DOWN at/under pinchDownDist, lifts at/over
    // pinchUpDist (hysteresis gap stops boundary chatter).
    pinchDownDist: 0.05,
    pinchUpDist: 0.08,
    fingertipLandmark: 8,
    thumbtipLandmark: 4,
    minDetectionConfidence: 0.5,
    smoothing: 0.5,
  },
  pen: {
    minStrokePoints: 4,
    minSampleDistance: 0.004,
    maxSampleHz: 90,
  },
  factorsPath: "/draw_factors.json",
  report: {
    topFactorCount: 6,
    sectionPopoutTtlMs: 9000,
  },
  asmr: {
    rate: 0.85,
    pitch: 0.9,
    volume: 0.9,
    promptSpacingMs: 12000,
  },
  // Auto walk-up: invite a detected, close, locked-on person to draw.
  walkup: {
    enabled: true,
    minLockedMs: 2500,
    cooldownMs: 45000,
    maxDistanceM: 1.6,
  },
  // Faint background bed, randomised per load from Nathan's dronefactory24 tracks.
  music: {
    enabled: true,
    volume: 0.12,
    tracks: ["/audio/ambient1.mp3", "/audio/ambient2.mp3"],
  },
  // One-tap .ics: three daily destress self-analysis reminders.
  reminders: {
    times: ["09:00", "14:00", "20:00"],
    title: "Draw and destress (eyesonme.ch)",
    url: "https://eyesonme.ch",
    durationMin: 5,
  },
  theme: "paper",
  coexist: {
    pipCorner: "top-right",
    pipScale: 0.26,
  },
};
